import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { getMessage } from '@tcc/i18n';
import Card from '../../../components/cards/cardAndPreview/Card';
import { useAppSelector } from '../../../utils';
import {
  getCurrentPlantingData,
  getField,
  getCurrentSeason,
} from '../../../selectors';
import {
  hasScripting,
  onClickPrescriptions,
  onClickPlantingReports,
  hasPlantingReport,
} from '../../../utils/fieldFeatures';
import { FeatureFlagKey } from '../../../types';
import { localizeUnits } from '../../../utils/processUnits';
import { PlantingRow } from './PlantingRow';
import { HybridsList } from './HybridsList';
import { trackEvent } from '../../../actions/analyticsActions';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';

interface PlantingCardProps {
  index: number;
}

export const PlantingCard = ({ index }: PlantingCardProps) => {
  const isScriptCreatorAPlugin = useFeatureFlag(FeatureFlagKey.SCRIPT_CREATOR_PLUGIN);
  const testId = 'dmb-planting';
  const dispatch = useDispatch();
  const state = useAppSelector((state) => state);
  const currentSeasonTag = getCurrentSeason(state, { index });
  const field = getField(state);

  const {
    area,
    population,
    seedProducts,
  } = getCurrentPlantingData(state, { index }) ?? {};

  const locArea = useMemo(
    () =>
      area &&
      localizeUnits(
        area.q,
        { key: area.u, localeGroup: 'area' },
        { precision: 1 }
      ),
    [area]
  );

  const locPopulation = useMemo(
    () =>
      population &&
      localizeUnits(
        population.q,
        { key: population.u, localeGroup: population.localeGroup },
        { precision: 0 }
      ),
    [population]
  );

  const hasPlantingData = useMemo(
    () => !!locArea && !!locPopulation,
    [locArea, locPopulation]
  );

  const viewLink = useMemo(() => {
    if(hasPlantingData && hasPlantingReport(field.features)) {
      return getMessage('card.planting.viewPlantingReport');
    }

    if(hasScripting(field.features)) {
      return getMessage('card.planting.viewPrescriptions');
    }
        
    return '';
  }, [hasPlantingData, field.features]);

  const sortedSeedProducts = useMemo(
    () =>
      seedProducts?.sort((a, b) => b.plantedArea - a.plantedArea) ?? [],
    [seedProducts]
  );

  const title = getMessage('layer.planting');

  const handleViewClick = useCallback(() => {
    if(hasPlantingData) {
      dispatch(trackEvent('report_planting_context_card', 'clicked'));
      onClickPlantingReports(currentSeasonTag)();
    } else {
      dispatch(trackEvent('prescriptions_planting_context_card', 'clicked'));
      onClickPrescriptions(field.id ?? '', isScriptCreatorAPlugin)();
    }
  }, [hasPlantingData, dispatch, currentSeasonTag, field, isScriptCreatorAPlugin]);

  return (
    <Card
      title={title}
      testId={testId}
      link={viewLink}
      onClickLink={handleViewClick}
    >
      <PlantingRow area={locArea} population={locPopulation} />
      <HybridsList seedProducts={sortedSeedProducts} />
    </Card>
  );
}